/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';

import { Observable } from 'rxjs';

import { REQUEST } from '@shared/data-access/constants';
import { Environment } from '@shared/data-access/interfaces';

@Injectable()
export class PlatformInterceptor implements HttpInterceptor {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly transferState = inject(TransferState);
	private readonly platformStateKey = makeStateKey<{ [x: string]: string }>(
		'DSG-ADDITIONAL-HEADERS'
	);
	private readonly request = inject(REQUEST, { optional: true }) as {
		header: (key: string) => string | null;
		headers: { [x: string]: string };
	};
	private readonly environment = inject(Environment);

	/**
	 * @description
	 * An Interceptor for all HTTP Requests that forwards the X-DSG-Platform header
	 * to HttpClient requests if available.
	 */
	public intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		let clonedRequest = request.clone();
		let headers = clonedRequest.headers;

		const matchedUrl = (this.environment.platform?.forwardedHeadersMatchers || []).some(
			(allowedUrl) => request.url.match(allowedUrl)
		);

		if (isPlatformBrowser(this.platformId)) {
			const platformStateHeaders = this.transferState.get(this.platformStateKey, null);

			if (!platformStateHeaders || !matchedUrl) {
				return next.handle(clonedRequest);
			}

			Object.entries(platformStateHeaders).forEach(([header, value]) => {
				headers = headers.append(header, value);
			});
		} else {
			const forwardedHeaders = this.getFilteredHeaders(this.request.headers, [
				'X-DSG-Platform'
			]);

			if (Object.keys(forwardedHeaders).length === 0 || !matchedUrl) {
				return next.handle(clonedRequest);
			}

			Object.entries(forwardedHeaders).forEach(([header, value]) => {
				headers = headers.append(header, value);
			});

			this.transferState.set(this.platformStateKey, forwardedHeaders);
		}

		clonedRequest = clonedRequest.clone({
			headers
		});

		return next.handle(clonedRequest);
	}

	private getFilteredHeaders = (headers: { [x: string]: string }, allowedHeaders: string[]) => {
		// Filters the headers to only include the allowed headers
		const filteredHeaders: Record<string, string> = {};
		const normalizedAllowedHeaders = allowedHeaders.map((header) => header.toUpperCase());

		Object.keys(headers).forEach((key) => {
			if (normalizedAllowedHeaders.includes(key.toUpperCase())) {
				filteredHeaders[key] = `${headers[key]}`;
			}
		});

		return filteredHeaders;
	};
}
